import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRefresh } from '../contexts/RefreshContext';

const CustomPullToRefresh = ({ children, className = '' }) => {
  const { refresh, isRefreshing } = useRefresh();
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const containerRef = useRef(null);
  const threshold = 80;

  const handleTouchStart = useCallback((e) => {
    if (window.scrollY === 0 && !isRefreshing) {
      setStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    if (diff > 0 && window.scrollY === 0) {
      setCurrentY(currentY);
      setPullDistance(Math.min(diff, threshold * 1.5));
      e.preventDefault();
    }
  }, [isPulling, isRefreshing, startY]);

  const handleTouchEnd = useCallback(() => {
    if (!isPulling || isRefreshing) return;
    
    const diff = currentY - startY;
    
    if (diff > threshold && window.scrollY === 0) {
      refresh();
    }
    
    setIsPulling(false);
    setPullDistance(0);
    setCurrentY(0);
    setStartY(0);
  }, [isPulling, isRefreshing, currentY, startY, threshold, refresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const pullProgress = Math.min(pullDistance / threshold, 1);
  const rotation = pullProgress * 180;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Pull to refresh indicator */}
      <div 
        className={`fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white text-center py-3 transition-all duration-200 ${
          isPulling || isRefreshing ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{
          transform: `translateY(${isPulling ? Math.min(pullDistance, threshold) - 100 : -100}px)`
        }}
      >
        <div className="flex items-center justify-center space-x-2">
          {isRefreshing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              <span className="font-medium">Refreshing...</span>
            </>
          ) : (
            <>
              <svg 
                className="w-5 h-5 transition-transform duration-200" 
                style={{ transform: `rotate(${rotation}deg)` }}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="font-medium">
                {pullDistance > threshold ? 'Release to refresh' : 'Pull to refresh'}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Main content with pull effect — only apply transform while pulling so fixed modals aren't trapped */}
      <div 
        className="transition-transform duration-200"
        style={isPulling ? { 
          transform: `translateY(${Math.min(pullDistance * 0.3, 20)}px)` 
        } : undefined}
      >
        {children}
      </div>
    </div>
  );
};

export default CustomPullToRefresh;

